/*
 * Animação do HERO da landing — "do celular ao ar".
 *
 * Conta o produto em um ciclo de ~27 s, desenhado num único <canvas>:
 *   1. o público manda mensagens (faíscas chegando ao celular);
 *   2. cada mensagem passa pela moderação — aprovada (✓) segue, spam (×) é barrado;
 *   3. as aprovadas entram na TV "AO VIVO";
 *   4. a TV vai para o estúdio ("NO AR") e apresenta lâminas no layout da
 *      Vitrine do Mural (cabeçalho com foto + zap + nome, logo, mídia/texto,
 *      dots), trocadas por uma vinheta de emissora;
 *   5. a TV volta e o ciclo recomeça.
 *
 * Tudo é código (sem vídeo/GIF): fica nítido em qualquer tela e pesa poucos KB.
 * Medidas em "u" = 1% da largura do palco; o canvas é maior que a caixa do
 * palco (ver HeroAoVivo.jsx) para os brilhos não serem cortados nas bordas.
 *
 * Pausa sozinho fora da tela ou com a aba escondida. Com
 * `prefers-reduced-motion`, desenha um quadro parado (primeira lâmina no ar).
 *
 * Retorna a função de limpeza (para o useEffect).
 */

export function iniciarHeroAoVivo(canvas, opts = {}) {
  const ACC = opts.accent || [74, 223, 131];
  const TEAL = [41, 193, 170];
  const RED = [248, 113, 113];
  const WHITE = [255, 255, 255];
  const rgba = (c, a) => `rgba(${c[0]},${c[1]},${c[2]},${a})`;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const main = canvas.getContext('2d');
  // jsdom (testes) e navegadores sem canvas: sem contexto, sem animação
  if (!main) return () => {};
  let ctx = main;                                    // trocado temporariamente para desenhar na tela da TV
  // a tela da TV é desenhada fora (off) e depois copiada — também para o reflexo no chão (refl)
  const off = document.createElement('canvas'), octx = off.getContext('2d');
  const refl = document.createElement('canvas'), rctx = refl.getContext('2d');
  let dpr = 1;

  // 5 mensagens por ciclo (4 aprovadas + 1 spam) e depois a apresentação das lâminas
  const MSGS = [
    { n: 'Ana', t: 'Alô, Recife! 👋' },
    { n: 'Carlos', t: 'Que golaço! ⚽' },
    { n: '?', t: 'GANHE R$500 >>', spam: true },
    { n: 'Sandra', t: 'Show incrível! 🎶' },
    { n: 'João', t: 'Ouvindo daqui 🎧' },
    { n: 'Maria', t: 'Salve, Manaus!' },
    { n: 'Pedro', t: 'Amo o programa 💚' },
    { n: '?', t: 'clique aqui $$$', spam: true },
    { n: 'Fernanda', t: 'Voto na opção B' },
    { n: 'Bruno', t: 'Bom dia, equipe! ☀️' },
  ];
  const PER_CYCLE = 5;

  // lâminas no formato da Vitrine do Mural: 16:9, cabeçalho com foto+nome, logo do canal e dots
  const LAMINAS = [
    { n: 'Ana Ribeiro', city: 'Recife, PE', t: 'Assistindo da praia de Boa Viagem! Mandem um alô ☀️', photo: 'https://i.pravatar.cc/120?img=32', scene: 'praia' },
    { n: 'Carlos Souza', city: 'São Paulo, SP', t: 'Que golaço! O estádio inteiro tá cantando junto com vocês ⚽', photo: 'https://i.pravatar.cc/120?img=12', scene: null },
    { n: 'Sandra Kato', city: 'Manaus, AM', t: 'Show incrível! Manda um alô pra galera do Norte 🎶', photo: 'https://i.pravatar.cc/120?img=47', scene: 'show' },
  ];
  // no modo parado (reduced motion) o quadro já foi desenhado quando as fotos chegam: redesenha
  const aoCarregarFoto = () => { if (reduce && S) draw(); };
  for (const l of LAMINAS) { l.img = new Image(); l.img.onload = aoCarregarFoto; l.img.src = l.photo; }

  // tempos em ms
  const INTRO = 1500, EVERY = 1700, TYPE = 500, TO_GATE = 700, DWELL = 650, TO_TV = 800, DROP = 600;
  const SHOW_IN = 1400, LAM = 3400, STING = 1000, SHOW_OUT = 1200;
  // trocas de conteúdo na tela durante o 'show': vazio -> 0 -> 1 -> 2 -> vazio, cada uma coberta pela vinheta
  const SWITCH = [0, 1, 2, 3].map(j => STING / 2 + j * LAM);
  const SHOW_LEN = SWITCH[3] + STING / 2;

  let W = 0, H = 0, u = 1, ox = 0, oy = 0;
  let P0, G0, T0, S0, P, G, T, S;
  let clock = 0, nextAt = INTRO - 300, idx = 0, typingUntil = -1, spawned = 0;
  let scene = 'feed', sceneT = 0, idleT = 0;
  let events = [], chat = [], flights = [], cards = [], parts = [], streams = [], dust = [], waves = [];
  let tvGlow = 0, phoneGlow = 0, gateFlash = { c: ACC, a: 0 }, total = 1248, shownTotal = 1248;
  let mx = 0, my = 0, tmx = 0, tmy = 0;
  let raf = 0, last = 0, running = false, nextStream = 0;

  const ease = t => (t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2);
  const easeOut = t => 1 - Math.pow(1 - t, 3);
  const easeInOutQuint = t => (t < 0.5 ? 16 * t ** 5 : 1 - Math.pow(-2 * t + 2, 5) / 2);
  const back = t => { const c1 = 1.9, c3 = c1 + 1; return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2); };
  const clamp01 = t => Math.max(0, Math.min(1, t));
  const lerp = (a, b, t) => a + (b - a) * t;
  const rnd = (a, b) => a + Math.random() * (b - a);
  const qb = (a, c, b, t) => {
    const v = 1 - t;
    return { x: v * v * a.x + 2 * v * t * c.x + t * t * b.x, y: v * v * a.y + 2 * v * t * c.y + t * t * b.y };
  };
  const font = (w, s) => `${w} ${s * u}px Inter, system-ui, sans-serif`;
  const later = (ms, fn) => events.push({ at: clock + ms, fn });
  const intro = (start, dur) => easeOut(clamp01((clock - start) / dur));

  // pseudo-aleatório com semente, para a plateia e as luzes não "tremerem" a cada quadro
  function seeded(seed) { return () => ((seed = (seed * 16807) % 2147483647) - 1) / 2147483646; }
  const CROWD = (() => { const r = seeded(7); return Array.from({ length: 90 }, () => ({ x: r(), y: r(), c: r(), s: r() })); })();
  const HEADS = (() => { const r = seeded(11); return Array.from({ length: 26 }, (_, i) => ({ x: i / 25 + (r() - 0.5) * 0.03, s: 0.8 + r() * 0.5, hand: r() < 0.35, ph: r() * 6 })); })();

  function layout() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    W = canvas.offsetWidth; H = canvas.offsetHeight;
    canvas.width = W * dpr; canvas.height = H * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const stageW = W / 1.44, stageH = H / 1.28;
    u = stageW / 100;
    ox = (W - stageW) / 2;
    oy = (H - stageH) / 2 + (stageH / u - 172) / 2 * u;
    P0 = { x: ox + 22 * u, y: oy + 2 * u, w: 56 * u, h: 74 * u };
    G0 = { y: oy + 96 * u, x0: ox + 6 * u, x1: ox + 94 * u };
    T0 = { x: ox + 5 * u, y: oy + 112 * u, w: 90 * u, h: 52 * u };
    S0 = { x: ox - 8 * u, y: oy + 56 * u, w: 116 * u, h: 116 * u * 9 / 16 };   // TV no estúdio (16:9), um pouco mais larga que o palco
    off.width = refl.width = Math.ceil(S0.w * dpr); off.height = refl.height = Math.ceil(S0.h * dpr);
    dust = Array.from({ length: 46 }, () => ({ x: rnd(0, W), y: rnd(0, H), r: rnd(0.3, 0.9) * u, s: rnd(0.004, 0.012) * u, a: rnd(0.08, 0.3), ph: rnd(0, 6.28) }));
  }

  function rr(x, y, w, h, r) {
    ctx.beginPath();
    ctx.moveTo(x + r, y); ctx.arcTo(x + w, y, x + w, y + h, r); ctx.arcTo(x + w, y + h, x, y + h, r);
    ctx.arcTo(x, y + h, x, y, r); ctx.arcTo(x, y, x + w, y, r); ctx.closePath();
  }
  function fit(text, maxW) {
    if (ctx.measureText(text).width <= maxW) return text;
    let s = text;
    while (s.length > 1 && ctx.measureText(s + '…').width > maxW) s = s.slice(0, -1);
    return s + '…';
  }
  function wrap(text, maxW) {
    const words = text.split(' '), lines = [];
    let line = '';
    for (const w of words) {
      const test = line ? line + ' ' + w : w;
      if (ctx.measureText(test).width > maxW && line) { lines.push(line); line = w; } else line = test;
    }
    if (line) lines.push(line);
    return lines;
  }
  const MAXT = 38;
  function bubbleW(m) { ctx.font = font(500, 4.4); return Math.min(ctx.measureText(m.t).width, MAXT * u) + 6.4 * u; }

  function glow(x, y, r, c, a) {
    if (a <= 0.002 || r <= 0) return;
    const g = ctx.createRadialGradient(x, y, 0, x, y, r);
    g.addColorStop(0, rgba(c, a)); g.addColorStop(1, rgba(c, 0));
    ctx.fillStyle = g; ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }

  function glass(x, y, w, h, r, lit, alpha) {
    if (alpha <= 0.01) return;
    ctx.save(); ctx.globalAlpha = alpha;
    ctx.shadowColor = rgba(ACC, 0.12 + 0.45 * lit); ctx.shadowBlur = (18 + 30 * lit);
    rr(x, y, w, h, r);
    const bg = ctx.createLinearGradient(x, y, x + w, y + h);
    bg.addColorStop(0, '#151a17'); bg.addColorStop(1, '#0a0b0a');
    ctx.fillStyle = bg; ctx.fill();
    ctx.shadowBlur = 0;
    const st = ctx.createLinearGradient(x, y, x + w, y + h);
    st.addColorStop(0, rgba(ACC, 0.55 + 0.4 * lit)); st.addColorStop(0.45, 'rgba(255,255,255,0.08)');
    st.addColorStop(1, rgba(TEAL, 0.35 + 0.4 * lit));
    ctx.strokeStyle = st; ctx.lineWidth = 1.2; ctx.stroke();
    ctx.save(); rr(x, y, w, h, r); ctx.clip();
    const sh = ctx.createLinearGradient(x, y, x + w * 0.6, y + h * 0.35);
    sh.addColorStop(0, 'rgba(255,255,255,0.06)'); sh.addColorStop(1, 'rgba(255,255,255,0)');
    ctx.fillStyle = sh; ctx.fillRect(x, y, w, h);
    ctx.restore();
    ctx.restore();
  }

  function bubble(x, y, m, { alpha = 1, scale = 1, rot = 0, stroke, lit = 0 } = {}) {
    const w = bubbleW(m), h = 9.4 * u;
    ctx.save();
    ctx.globalAlpha = alpha; ctx.translate(x, y); ctx.rotate(rot); ctx.scale(scale, scale);
    if (lit) { ctx.shadowColor = stroke || rgba(ACC, 0.8); ctx.shadowBlur = 22 * lit; }
    rr(-w / 2, -h / 2, w, h, 2.8 * u);
    const g = ctx.createLinearGradient(0, -h / 2, 0, h / 2);
    g.addColorStop(0, '#173a26'); g.addColorStop(1, '#0f2419');
    ctx.fillStyle = g; ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = stroke || rgba(ACC, 0.4); ctx.lineWidth = 1; ctx.stroke();
    ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.font = font(500, 4.4);
    ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
    ctx.fillText(fit(m.t, MAXT * u), -w / 2 + 3.2 * u, 0.2 * u);
    ctx.restore();
    return w;
  }

  function badge(x, y, ok, s, alpha) {
    if (s <= 0) return;
    const r = 3.2 * u * s, c = ok ? ACC : RED;
    ctx.save(); ctx.globalAlpha = alpha;
    ctx.shadowColor = rgba(c, 0.9); ctx.shadowBlur = 14;
    ctx.fillStyle = rgba(c, 1);
    ctx.beginPath(); ctx.arc(x, y, r, 0, 6.283); ctx.fill();
    ctx.shadowBlur = 0;
    ctx.strokeStyle = '#0a0a0a'; ctx.lineWidth = Math.max(1.4, 0.8 * u * s); ctx.lineCap = 'round'; ctx.lineJoin = 'round';
    ctx.beginPath();
    if (ok) { ctx.moveTo(x - r * 0.45, y); ctx.lineTo(x - r * 0.1, y + r * 0.38); ctx.lineTo(x + r * 0.5, y - r * 0.35); }
    else { const k = r * 0.36; ctx.moveTo(x - k, y - k); ctx.lineTo(x + k, y + k); ctx.moveTo(x + k, y - k); ctx.lineTo(x - k, y + k); }
    ctx.stroke(); ctx.restore();
  }

  function burst(x, y, c, n, speed, life) {
    for (let i = 0; i < n; i++) {
      const a = rnd(0, 6.283), s = rnd(0.25, 1) * speed * u;
      parts.push({ x, y, vx: Math.cos(a) * s, vy: Math.sin(a) * s, t: 0, max: rnd(0.6, 1) * life, c, r: rnd(0.35, 0.9) * u });
    }
  }

  // ================= cenas desenhadas das lâminas =================
  function scenePraia(b, t) {
    const { x, y, w, h } = b;
    const sky = ctx.createLinearGradient(0, y, 0, y + h * 0.6);
    sky.addColorStop(0, '#1e1b4b'); sky.addColorStop(0.45, '#7c3aed'); sky.addColorStop(0.8, '#f472b6'); sky.addColorStop(1, '#fb923c');
    ctx.fillStyle = sky; ctx.fillRect(x, y, w, h * 0.6);
    glow(x + w * 0.5, y + h * 0.56, w * 0.55, [251, 191, 36], 0.55);
    ctx.fillStyle = '#fde68a'; ctx.beginPath(); ctx.arc(x + w * 0.5, y + h * 0.56, w * 0.13, 0, 6.283); ctx.fill();
    const sea = ctx.createLinearGradient(0, y + h * 0.58, 0, y + h);
    sea.addColorStop(0, '#312e81'); sea.addColorStop(1, '#020617');
    ctx.fillStyle = sea; ctx.fillRect(x, y + h * 0.58, w, h * 0.42);
    for (let i = 0; i < 16; i++) {
      const yy = y + h * (0.6 + i * 0.022), ww = w * (0.32 - i * 0.012) * (1 + 0.15 * Math.sin(t * 0.003 + i));
      ctx.fillStyle = `rgba(253,186,116,${0.7 - i * 0.04})`;
      ctx.fillRect(x + w * 0.5 - ww / 2 + Math.sin(t * 0.002 + i * 1.7) * w * 0.02, yy, ww, Math.max(1, h * 0.005));
    }
    // coqueiro em silhueta
    ctx.fillStyle = '#0b0718';
    ctx.beginPath(); ctx.moveTo(x + w * 0.1, y + h); ctx.quadraticCurveTo(x + w * 0.16, y + h * 0.6, x + w * 0.24, y + h * 0.36);
    ctx.lineTo(x + w * 0.255, y + h * 0.365); ctx.quadraticCurveTo(x + w * 0.19, y + h * 0.62, x + w * 0.14, y + h); ctx.fill();
    for (let i = 0; i < 6; i++) {
      const a = -2.6 + i * 0.75 + Math.sin(t * 0.0015 + i) * 0.05;
      ctx.save(); ctx.translate(x + w * 0.245, y + h * 0.36); ctx.rotate(a);
      ctx.beginPath(); ctx.ellipse(w * 0.11, 0, w * 0.12, w * 0.022, 0.15, 0, 6.283); ctx.fill(); ctx.restore();
    }
  }
  function sceneEstadio(b, t) {
    const { x, y, w, h } = b;
    const sky = ctx.createLinearGradient(0, y, 0, y + h * 0.45);
    sky.addColorStop(0, '#020617'); sky.addColorStop(1, '#0c1f3f');
    ctx.fillStyle = sky; ctx.fillRect(x, y, w, h);
    // arquibancada com a torcida
    ctx.fillStyle = '#0b1324'; ctx.fillRect(x, y + h * 0.3, w, h * 0.22);
    for (const c of CROWD) {
      const cols = ['#ef4444', '#facc15', '#f8fafc', '#22c55e', '#3b82f6'];
      ctx.fillStyle = cols[(c.c * cols.length) | 0];
      ctx.globalAlpha = 0.35 + 0.5 * (0.5 + 0.5 * Math.sin(t * 0.008 + c.s * 20));
      ctx.fillRect(x + c.x * w, y + h * (0.31 + c.y * 0.19), w * 0.012, w * 0.012);
    }
    ctx.globalAlpha = 1;
    // gramado
    const f = ctx.createLinearGradient(0, y + h * 0.5, 0, y + h);
    f.addColorStop(0, '#15803d'); f.addColorStop(1, '#14532d');
    ctx.fillStyle = f;
    ctx.beginPath(); ctx.moveTo(x + w * 0.12, y + h * 0.5); ctx.lineTo(x + w * 0.88, y + h * 0.5); ctx.lineTo(x + w * 1.3, y + h); ctx.lineTo(x - w * 0.3, y + h); ctx.fill();
    ctx.strokeStyle = 'rgba(255,255,255,0.55)'; ctx.lineWidth = Math.max(1, w * 0.006);
    ctx.beginPath(); ctx.moveTo(x + w * 0.5, y + h * 0.5); ctx.lineTo(x + w * 0.5, y + h); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(x + w * 0.5, y + h * 0.66, w * 0.17, h * 0.045, 0, 0, 6.283); ctx.stroke();
    // refletores
    ctx.save(); ctx.globalCompositeOperation = 'lighter';
    for (const lx of [0.12, 0.88]) {
      const px = x + w * lx, py = y + h * 0.1;
      const beam = ctx.createLinearGradient(px, py, x + w * 0.5, y + h * 0.75);
      beam.addColorStop(0, 'rgba(255,255,255,0.28)'); beam.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = beam;
      ctx.beginPath(); ctx.moveTo(px, py); ctx.lineTo(x + w * (lx < 0.5 ? 0.35 : 0.3), y + h * 0.9); ctx.lineTo(x + w * (lx < 0.5 ? 0.7 : 0.65), y + h * 0.9); ctx.fill();
      glow(px, py, w * 0.22, WHITE, 0.55 + 0.1 * Math.sin(t * 0.01 + lx * 9));
    }
    ctx.restore();
  }
  function sceneShow(b, t) {
    const { x, y, w, h } = b;
    const bg = ctx.createLinearGradient(0, y, 0, y + h);
    bg.addColorStop(0, '#2e1065'); bg.addColorStop(1, '#0f0a1f');
    ctx.fillStyle = bg; ctx.fillRect(x, y, w, h);
    ctx.save(); ctx.globalCompositeOperation = 'lighter';
    const beams = [[236, 72, 153], [34, 211, 238], [168, 85, 247], [74, 223, 131], [250, 204, 21]];
    beams.forEach((c, i) => {
      const px = x + w * (0.1 + i * 0.2), py = y + h * 0.02;
      const ang = Math.sin(t * 0.0012 + i * 1.3) * 0.45;
      ctx.save(); ctx.translate(px, py); ctx.rotate(ang);
      const g = ctx.createLinearGradient(0, 0, 0, h * 0.9);
      g.addColorStop(0, rgba(c, 0.55)); g.addColorStop(1, rgba(c, 0));
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.moveTo(-w * 0.01, 0); ctx.lineTo(w * 0.01, 0); ctx.lineTo(w * 0.12, h * 0.9); ctx.lineTo(-w * 0.12, h * 0.9); ctx.fill();
      ctx.restore();
      glow(px, py, w * 0.08, c, 0.8);
    });
    glow(x + w * 0.5, y + h * 0.72, w * 0.6, [168, 85, 247], 0.35);
    ctx.restore();
    // plateia em silhueta, com mãos para cima
    ctx.fillStyle = '#05030a';
    ctx.fillRect(x, y + h * 0.86, w, h * 0.14);
    for (const hd of HEADS) {
      const hx = x + hd.x * w, hy = y + h * 0.86 + Math.sin(t * 0.006 + hd.ph) * h * 0.004;
      ctx.beginPath(); ctx.arc(hx, hy, w * 0.035 * hd.s, 0, 6.283); ctx.fill();
      if (hd.hand) {
        const sw = Math.sin(t * 0.005 + hd.ph) * w * 0.02;
        ctx.beginPath(); ctx.moveTo(hx - w * 0.008, hy); ctx.lineTo(hx + sw - w * 0.006, hy - h * 0.12); ctx.lineTo(hx + sw + w * 0.006, hy - h * 0.12); ctx.lineTo(hx + w * 0.008, hy); ctx.fill();
      }
    }
  }
  const SCENES = { praia: scenePraia, estadio: sceneEstadio, show: sceneShow };

  // fundo de estúdio da lâmina (o "tema" da vitrine)
  function studioBg(b, t, k) {
    const g = ctx.createLinearGradient(b.x, b.y, b.x + b.w, b.y + b.h);
    g.addColorStop(0, '#071a33'); g.addColorStop(0.55, '#0a2b3d'); g.addColorStop(1, '#06302a');
    ctx.fillStyle = g; ctx.fillRect(b.x, b.y, b.w, b.h);
    ctx.save(); ctx.globalCompositeOperation = 'lighter';
    glow(b.x + b.w * 0.85, b.y + b.h * 0.15, b.w * 0.5, TEAL, 0.22);
    glow(b.x + b.w * 0.1, b.y + b.h * 0.95, b.w * 0.45, ACC, 0.14);
    // feixes diagonais de luz, bem lentos
    for (let i = 0; i < 3; i++) {
      const x0 = b.x + ((t * 0.02 * (i + 1) + i * b.w * 0.4) % (b.w * 1.6)) - b.w * 0.3;
      const lg = ctx.createLinearGradient(x0, 0, x0 + b.w * 0.12, 0);
      lg.addColorStop(0, 'rgba(255,255,255,0)'); lg.addColorStop(0.5, 'rgba(255,255,255,0.05)'); lg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = lg;
      ctx.beginPath(); ctx.moveTo(x0, b.y); ctx.lineTo(x0 + b.w * 0.12, b.y); ctx.lineTo(x0 - b.w * 0.1, b.y + b.h); ctx.lineTo(x0 - b.w * 0.22, b.y + b.h); ctx.fill();
    }
    ctx.restore();
    // linhas finas de "grafismo"
    ctx.strokeStyle = 'rgba(255,255,255,0.05)'; ctx.lineWidth = 1;
    for (let i = 0; i < 6; i++) { const yy = b.y + b.h * (0.2 + i * 0.13); ctx.beginPath(); ctx.moveTo(b.x, yy); ctx.lineTo(b.x + b.w, yy); ctx.stroke(); }
  }

  // uma lâmina no layout da Vitrine: cabeçalho (foto + zap + nome), logo/AO VIVO, mídia e texto, dots
  function drawLamina(i, b, lt) {
    const L = LAMINAS[i], k = b.w / 100;
    const step = (d) => easeOut(clamp01((lt - d) / 420));
    studioBg(b, clock, k);

    // cabeçalho
    let a = step(200);
    ctx.save(); ctx.globalAlpha = a; ctx.translate((1 - a) * -4 * k, 0);
    const ax = b.x + 7 * k, ay = b.y + 7 * k, ar = 3.8 * k;
    ctx.save(); ctx.beginPath(); ctx.arc(ax, ay, ar, 0, 6.283); ctx.clip();
    if (L.img.complete && L.img.naturalWidth) ctx.drawImage(L.img, ax - ar, ay - ar, ar * 2, ar * 2);
    else { ctx.fillStyle = rgba(ACC, 0.9); ctx.fillRect(ax - ar, ay - ar, ar * 2, ar * 2); }
    ctx.restore();
    ctx.strokeStyle = 'rgba(255,255,255,0.7)'; ctx.lineWidth = Math.max(1.2, 0.35 * k);
    ctx.beginPath(); ctx.arc(ax, ay, ar, 0, 6.283); ctx.stroke();
    ctx.fillStyle = '#25d366'; ctx.beginPath(); ctx.arc(ax + ar * 0.75, ay + ar * 0.75, 1.4 * k, 0, 6.283); ctx.fill();
    ctx.strokeStyle = '#fff'; ctx.lineWidth = Math.max(1, 0.3 * k);
    ctx.beginPath(); ctx.arc(ax + ar * 0.75, ay + ar * 0.75, 0.6 * k, 0.6, 5.6); ctx.stroke();
    ctx.fillStyle = '#fff'; ctx.font = `700 ${3.3 * k}px Inter, system-ui, sans-serif`; ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
    ctx.fillText(L.n, ax + ar + 2.6 * k, ay - 1.4 * k);
    ctx.fillStyle = 'rgba(255,255,255,0.65)'; ctx.font = `500 ${2.4 * k}px Inter, system-ui, sans-serif`;
    ctx.fillText(L.city, ax + ar + 2.6 * k, ay + 2.2 * k);
    ctx.restore();

    // logo do canal + AO VIVO
    ctx.save();
    ctx.font = `800 ${3.6 * k}px Inter, system-ui, sans-serif`; ctx.textAlign = 'right'; ctx.textBaseline = 'middle';
    ctx.fillStyle = rgba(ACC, 1); ctx.fillText('youon', b.x + b.w - 5 * k, b.y + 6.4 * k);
    const lw = ctx.measureText('youon').width;
    ctx.font = `800 ${2 * k}px Inter, system-ui, sans-serif`;
    const pw = ctx.measureText('AO VIVO').width + 5 * k, px = b.x + b.w - 5 * k - lw - 2.5 * k - pw;
    rr(px, b.y + 4.3 * k, pw, 4.2 * k, 0.8 * k); ctx.fillStyle = '#e11d48'; ctx.fill();
    ctx.fillStyle = `rgba(255,255,255,${0.6 + 0.4 * Math.sin(clock * 0.008)})`;
    ctx.beginPath(); ctx.arc(px + 1.8 * k, b.y + 6.4 * k, 0.6 * k, 0, 6.283); ctx.fill();
    ctx.fillStyle = '#fff'; ctx.textAlign = 'left'; ctx.fillText('AO VIVO', px + 3 * k, b.y + 6.5 * k);
    ctx.restore();

    const quote = (x, y, s, al) => { ctx.fillStyle = rgba(ACC, al); ctx.font = `900 ${s}px Georgia, serif`; ctx.textAlign = 'left'; ctx.fillText('“', x, y); };
    if (L.scene) {
      // mídia à esquerda (com zoom lento) + mensagem à direita
      const mb = { x: b.x + 5 * k, y: b.y + 14 * k, w: 47 * k, h: 36 * k };
      a = step(80);
      ctx.save(); ctx.globalAlpha = a;
      rr(mb.x, mb.y, mb.w, mb.h, 1.4 * k); ctx.clip();
      const sc = 1.04 + 0.06 * clamp01(lt / LAM) + (1 - a) * 0.08;
      ctx.translate(mb.x + mb.w / 2, mb.y + mb.h / 2); ctx.scale(sc, sc); ctx.translate(-mb.x - mb.w / 2, -mb.y - mb.h / 2);
      SCENES[L.scene](mb, clock);
      ctx.restore();
      ctx.save(); ctx.globalAlpha = a; rr(mb.x, mb.y, mb.w, mb.h, 1.4 * k);
      ctx.strokeStyle = 'rgba(255,255,255,0.18)'; ctx.lineWidth = 1; ctx.stroke(); ctx.restore();

      ctx.font = `700 ${4.3 * k}px Inter, system-ui, sans-serif`;
      const lines = wrap(L.t, 38 * k).slice(0, 5), lh = 5.6 * k;
      const top = mb.y + mb.h / 2 - (lines.length * lh) / 2 + 2 * k;
      ctx.save(); ctx.globalAlpha = step(300); quote(b.x + 56 * k, top - 1.5 * k, 9 * k, 0.9); ctx.restore();
      ctx.font = `700 ${4.3 * k}px Inter, system-ui, sans-serif`; ctx.textBaseline = 'middle';
      lines.forEach((ln, j) => {
        const s = step(380 + j * 90);
        ctx.save(); ctx.globalAlpha = s; ctx.fillStyle = '#fff';
        ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 8;
        ctx.fillText(ln, b.x + 56 * k + (1 - s) * 4 * k, top + lh * (j + 0.5) + 3 * k);
        ctx.restore();
      });
    } else {
      // lâmina só de texto: mensagem grande e centralizada
      ctx.font = `800 ${6 * k}px Inter, system-ui, sans-serif`;
      const lines = wrap(L.t, 78 * k).slice(0, 3), lh = 7.6 * k;
      const top = b.y + 31 * k - (lines.length * lh) / 2 + 1 * k;
      ctx.save(); ctx.globalAlpha = step(250); ctx.textAlign = 'center';
      ctx.fillStyle = rgba(ACC, 0.9); ctx.font = `900 ${12 * k}px Georgia, serif`; ctx.textBaseline = 'middle';
      ctx.fillText('“', b.x + b.w / 2, top - 3 * k); ctx.restore();
      ctx.font = `800 ${6 * k}px Inter, system-ui, sans-serif`; ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      lines.forEach((ln, j) => {
        const s = step(350 + j * 110);
        ctx.save(); ctx.globalAlpha = s; ctx.fillStyle = '#fff';
        ctx.shadowColor = 'rgba(0,0,0,0.6)'; ctx.shadowBlur = 10;
        ctx.translate(b.x + b.w / 2, top + lh * (j + 0.5) + 4 * k); ctx.scale(0.92 + 0.08 * s, 0.92 + 0.08 * s);
        ctx.fillText(ln, 0, (1 - s) * 3 * k);
        ctx.restore();
      });
      ctx.textAlign = 'left';
    }

    // dots de navegação
    for (let d = 0; d < 3; d++) {
      const on = d === i, dx = b.x + b.w / 2 + (d - 1) * 4 * k;
      ctx.fillStyle = on ? '#fff' : 'rgba(255,255,255,0.35)';
      ctx.beginPath(); ctx.arc(dx, b.y + b.h - 3.2 * k, (on ? 1 : 0.7) * k, 0, 6.283); ctx.fill();
    }
  }

  // vinheta de emissora: faixas diagonais varrem a tela, com a logo no meio quando ela está coberta
  function drawStinger(b, p) {
    const W = b.w, e = p < 0.5 ? 4 * p ** 3 : 1 - Math.pow(-2 * p + 2, 3) / 2;
    const cx = b.x + W / 2 + lerp(-1.85 * W, 1.85 * W, e);
    const skew = b.h * 0.45;
    // faixas da frente para trás: branca, teal, VERDE (larga), teal, branca
    const bands = [[0.05, WHITE], [0.2, TEAL], [1.32, ACC], [0.2, TEAL], [0.05, WHITE]];
    const total = bands.reduce((s, x) => s + x[0], 0) * W;
    let x = cx + total / 2;
    for (const [bw, c] of bands) {
      const w = bw * W;
      ctx.fillStyle = rgba(c, 1);
      ctx.beginPath();
      ctx.moveTo(x + skew / 2, b.y); ctx.lineTo(x - w + skew / 2, b.y); ctx.lineTo(x - w - skew / 2, b.y + b.h); ctx.lineTo(x - skew / 2, b.y + b.h);
      ctx.closePath(); ctx.fill();
      x -= w;
    }
    // listras de velocidade na faixa verde
    ctx.save(); ctx.globalAlpha = 0.18; ctx.fillStyle = '#0a0a0a';
    for (let i = 0; i < 6; i++) {
      const yy = b.y + b.h * (0.12 + i * 0.15), lx = cx - W * 0.4 + ((i * 37) % 40) * W * 0.01;
      ctx.fillRect(lx, yy, W * (0.25 + (i % 3) * 0.1), Math.max(1, b.h * 0.012));
    }
    ctx.restore();
    // logo no meio
    const la = clamp01(1 - Math.abs(p - 0.5) / 0.22);
    if (la > 0) {
      const s = 0.8 + 0.2 * back(clamp01((p - 0.3) / 0.25));
      ctx.save(); ctx.globalAlpha = la;
      ctx.translate(b.x + W / 2, b.y + b.h / 2); ctx.scale(s, s);
      ctx.fillStyle = '#0a0a0a'; ctx.font = `900 ${b.w * 0.11}px Inter, system-ui, sans-serif`;
      ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
      ctx.fillText('youon', 0, 0);
      ctx.font = `700 ${b.w * 0.028}px Inter, system-ui, sans-serif`;
      ctx.fillText('AO VIVO  ·  PELO WHATSAPP', 0, b.w * 0.075);
      ctx.restore();
    }
    return { edge: cx + total / 2 };
  }

  // estado da tela no 'show': qual lâmina está no ar e se a vinheta está passando
  function screenState() {
    let idx = -1, from = 0;
    for (let j = 0; j < 4; j++) if (sceneT >= SWITCH[j]) { idx = j < 3 ? j : -1; from = SWITCH[j]; }
    let sting = -1;
    for (const s of SWITCH) if (Math.abs(sceneT - s) < STING / 2) sting = (sceneT - s + STING / 2) / STING;
    return { idx, lt: sceneT - from, sting };
  }

  // desenha o conteúdo da TV (lâmina + vinheta) no canvas fora de tela
  function renderScreen() {
    const b = { x: 0, y: 0, w: S0.w, h: S0.h };
    ctx = octx;
    octx.setTransform(dpr, 0, 0, dpr, 0, 0);
    octx.clearRect(0, 0, b.w, b.h);
    octx.save(); rr(0, 0, b.w, b.h, 2.4 * u); octx.clip();
    const st = scene === 'show' ? screenState() : { idx: -1, sting: -1 };
    if (st.idx >= 0) drawLamina(st.idx, b, st.lt);
    let edge = null;
    if (st.sting >= 0) edge = drawStinger(b, st.sting).edge;
    octx.restore();
    ctx = main;
    return { has: st.idx >= 0 || st.sting >= 0, edge };
  }

  const slotY = i => P.y + P.h - 9 * u - i * 12 * u;
  const phoneBubbleX = m => P.x + P.w - 4 * u - bubbleW(m) / 2;
  const cardH = () => 12.6 * u;
  const cardY = i => T.y + 13 * u + i * 14.4 * u;

  function spawn() {
    const m = MSGS[idx++ % MSGS.length];
    spawned += 1;
    typingUntil = clock + TYPE;
    later(TYPE, () => { chat.unshift({ m, k: 0, a: 0 }); if (chat.length > 7) chat.pop(); });
    later(TYPE + 300, () => { flights.push({ m, age: 0, trail: [] }); phoneGlow = Math.max(phoneGlow, 0.7); });
  }

  function spawnStream() {
    const side = Math.random();
    let p0;
    if (side < 0.4) p0 = { x: rnd(0, ox), y: rnd(0, oy + 90 * u) };
    else if (side < 0.8) p0 = { x: rnd(W - ox, W), y: rnd(0, oy + 90 * u) };
    else p0 = { x: rnd(0, W), y: rnd(0, oy) };
    const left = p0.x < P.x + P.w / 2;
    const p1 = { x: left ? P.x : P.x + P.w, y: rnd(P.y + 10 * u, P.y + P.h - 10 * u) };
    const c = { x: (p0.x + p1.x) / 2 + rnd(-15, 15) * u, y: Math.min(p0.y, p1.y) - rnd(0, 20) * u };
    streams.push({ p0, c, p1, t: 0, dur: rnd(900, 1600), col: Math.random() < 0.6 ? ACC : TEAL });
  }

  // m: 0 = TV normal, 1 = tela vertical das lâminas
  function morph() {
    if (scene === 'in') return easeInOutQuint(clamp01(sceneT / SHOW_IN));
    if (scene === 'show') return 1;
    if (scene === 'out') return 1 - easeInOutQuint(clamp01(sceneT / SHOW_OUT));
    return 0;
  }

  function setScene(s) { scene = s; sceneT = 0; }

  function update(dt) {
    clock += dt; sceneT += dt;
    mx = lerp(mx, tmx, Math.min(1, dt / 300)); my = lerp(my, tmy, Math.min(1, dt / 300));

    const m = morph();
    const sway = Math.sin(clock * 0.0006) * 0.6 * u * (1 - m);
    const pIn = intro(0, 800), gIn = intro(250, 800), tIn = intro(450, 900);
    P = { ...P0, x: P0.x + mx * 2.2 * u, y: P0.y + my * 1.6 * u + sway + (1 - pIn) * 10 * u - m * 18 * u, a: pIn * (1 - clamp01(m * 1.6)) };
    G = { ...G0, y: G0.y + my * 1 * u - m * 10 * u, a: gIn * (1 - clamp01(m * 2)) };
    T = { ...T0, x: T0.x - mx * 1.6 * u, y: T0.y - my * 1.2 * u - sway * 0.5 + (1 - tIn) * 12 * u, a: tIn };
    S = { ...S0, x: S0.x - mx * 1.6 * u, y: S0.y - my * 1.2 * u };

    // ---- roteiro
    if (scene === 'feed') {
      if (spawned < PER_CYCLE && clock >= nextAt) { spawn(); nextAt += EVERY; }
      if (spawned >= PER_CYCLE && !flights.length && !events.length) {
        idleT += dt;
        if (idleT > 900) { idleT = 0; setScene('in'); burst(T.x + T.w / 2, T.y + T.h / 2, ACC, 40, 0.16, 1000); }
      }
      if (clock > 400 && clock >= nextStream) { spawnStream(); nextStream = clock + rnd(40, 110); }
    } else if (scene === 'in' && sceneT >= SHOW_IN) setScene('show');
    else if (scene === 'show') {
      if (sceneT >= SHOW_LEN) setScene('out');
    } else if (scene === 'out' && sceneT >= SHOW_OUT) {
      setScene('feed'); spawned = 0; nextAt = clock + 300; chat = [];
    }
    events = events.filter(e => (clock >= e.at ? (e.fn(), false) : true));

    const typing = clock < typingUntil;
    chat.forEach((c, i) => { c.k = lerp(c.k, i + (typing ? 1 : 0), Math.min(1, dt / 120)); c.a = Math.min(1, c.a + dt / 250); });
    const incoming = flights.some(f => !f.m.spam && f.decided) ? 1 : 0;
    cards.forEach((c, i) => { c.k = lerp(c.k, i + incoming, Math.min(1, dt / 140)); c.a = Math.min(1, c.a + dt / 350); });
    cards = cards.filter(c => c.k < 3.6);

    for (const s of streams) {
      s.t += dt / s.dur;
      if (s.t >= 1 && !s.done) { s.done = true; phoneGlow = Math.min(1, phoneGlow + 0.06); total += 1; }
    }
    streams = streams.filter(s => !s.done);

    for (const f of flights) {
      f.age += dt;
      const after = f.age - TO_GATE - DWELL;
      if (!f.decided && f.age > TO_GATE + DWELL * 0.55) {
        f.decided = true;
        gateFlash = { c: f.m.spam ? RED : ACC, a: 1 };
        if (f.m.spam) burst(ox + 50 * u, G.y, RED, 26, 0.09, 700);
        else burst(ox + 50 * u, G.y, ACC, 10, 0.05, 500);
      }
      if (!f.m.spam && after >= TO_TV && !f.done) {
        f.done = true;
        tvGlow = 1;
        const tx = ox + 50 * u, ty = cardY(0) + cardH() / 2;
        burst(tx, ty, ACC, 34, 0.13, 900);
        waves.push({ x: tx, y: ty, t: 0 });
        cards.unshift({ m: f.m, k: 0, a: 0.3 });
      }
      if (f.m.spam && after >= DROP) f.done = true;
    }
    flights = flights.filter(f => !f.done);

    for (const p of parts) { p.t += dt; p.x += p.vx * dt; p.y += p.vy * dt; p.vx *= 0.985; p.vy = p.vy * 0.985 + 0.00012 * u * dt; }
    parts = parts.filter(p => p.t < p.max);
    for (const w of waves) w.t += dt / 900;
    waves = waves.filter(w => w.t < 1);

    shownTotal += (total - shownTotal) * Math.min(1, dt / 200);
    tvGlow = Math.max(0, tvGlow - dt / 1000);
    phoneGlow = Math.max(0, phoneGlow - dt / 700);
    gateFlash.a = Math.max(0, gateFlash.a - dt / 700);
  }

  function draw() {
    ctx.clearRect(0, 0, W, H);
    const m = morph();
    const R = {                                         // tela atual (TV -> vertical)
      x: lerp(T.x, S.x, m), y: lerp(T.y, S.y, m), w: lerp(T.w, S.w, m), h: lerp(T.h, S.h, m),
    };
    const cx = ox + 50 * u;
    const gatePos = { x: cx, y: G.y };
    const tvIn = { x: cx, y: cardY(0) + cardH() / 2 };
    const from = { x: cx, y: P.y + P.h + 1 * u };
    const c1 = { x: cx + 14 * u, y: (P.y + P.h + G.y) / 2 };
    const c2 = { x: cx - 16 * u, y: (G.y + tvIn.y) / 2 };
    const t = clock;
    const feedA = 1 - clamp01(m * 1.6);

    // ---- atmosfera
    ctx.save();
    ctx.globalCompositeOperation = 'lighter';
    const ia = intro(0, 1400);
    glow(P.x + P.w / 2 + Math.sin(t * 0.0004) * 5 * u - mx * 2 * u, P.y + P.h * 0.5 + Math.cos(t * 0.0005) * 5 * u, 52 * u, ACC, (0.14 * ia + 0.08 * phoneGlow) * feedA);
    glow(R.x + R.w / 2 + Math.cos(t * 0.00035) * 6 * u - mx * 2 * u, R.y + R.h * 0.5 + Math.sin(t * 0.0004) * 4 * u, lerp(58, 64, m) * u, TEAL, 0.13 * ia + 0.12 * tvGlow + 0.08 * m);
    glow(R.x + R.w / 2, R.y + R.h * 0.3, 50 * u * m, ACC, 0.1 * m);
    glow(cx, G.y, 40 * u, gateFlash.c, (0.05 * ia + 0.16 * gateFlash.a) * G.a);
    for (const d of dust) {
      d.y -= d.s * 16; if (d.y < -5) { d.y = H + 5; d.x = rnd(0, W); }
      ctx.fillStyle = `rgba(255,255,255,${d.a * (0.6 + 0.4 * Math.sin(t * 0.002 + d.ph)) * ia})`;
      ctx.beginPath(); ctx.arc(d.x + mx * 3 * u, d.y, d.r, 0, 6.283); ctx.fill();
    }
    for (const s of streams) {
      const e = ease(Math.min(1, s.t));
      const h = qb(s.p0, s.c, s.p1, e), b = qb(s.p0, s.c, s.p1, Math.max(0, e - 0.08));
      const a = Math.min(1, s.t * 4) * 0.85 * feedA;
      if (a <= 0.01) continue;
      const lg = ctx.createLinearGradient(b.x, b.y, h.x, h.y);
      lg.addColorStop(0, rgba(s.col, 0)); lg.addColorStop(1, rgba(s.col, a));
      ctx.strokeStyle = lg; ctx.lineWidth = 0.9; ctx.lineCap = 'round';
      ctx.beginPath(); ctx.moveTo(b.x, b.y); ctx.lineTo(h.x, h.y); ctx.stroke();
      ctx.fillStyle = rgba(s.col, a); ctx.beginPath(); ctx.arc(h.x, h.y, 0.55 * u, 0, 6.283); ctx.fill();
    }
    ctx.restore();

    // trilho
    ctx.save();
    ctx.globalAlpha = intro(600, 900) * feedA;
    ctx.setLineDash([2, 6]); ctx.lineDashOffset = -t * 0.03;
    ctx.strokeStyle = rgba(ACC, 0.28); ctx.lineWidth = 1.2;
    ctx.beginPath(); ctx.moveTo(cx, P.y + P.h); ctx.quadraticCurveTo(c1.x, c1.y, cx, G.y);
    ctx.quadraticCurveTo(c2.x, c2.y, cx, T.y); ctx.stroke();
    ctx.restore();

    // ---- celular
    if (P.a > 0.01) {
      glass(P.x, P.y, P.w, P.h, 7 * u, phoneGlow, P.a);
      ctx.save(); ctx.globalAlpha = P.a;
      rr(P.x + P.w / 2 - 7 * u, P.y + 3 * u, 14 * u, 2.4 * u, 1.2 * u);
      ctx.fillStyle = 'rgba(255,255,255,0.12)'; ctx.fill();
      ctx.fillStyle = rgba(ACC, 0.3);
      ctx.beginPath(); ctx.arc(P.x + 8 * u, P.y + 12 * u, 3.2 * u, 0, 6.283); ctx.fill();
      ctx.fillStyle = 'rgba(255,255,255,0.85)'; ctx.font = font(600, 3.4); ctx.textBaseline = 'middle'; ctx.textAlign = 'left';
      ctx.fillText('Programa ao vivo', P.x + 13.5 * u, P.y + 10.8 * u);
      ctx.fillStyle = rgba(ACC, 0.8); ctx.font = font(500, 2.8);
      ctx.fillText('online', P.x + 13.5 * u, P.y + 14.4 * u);
      ctx.fillStyle = 'rgba(255,255,255,0.07)'; ctx.fillRect(P.x + 2 * u, P.y + 18 * u, P.w - 4 * u, 1);
      ctx.save();
      rr(P.x + 1, P.y + 18.5 * u, P.w - 2, P.h - 19.5 * u, 6 * u); ctx.clip();
      const fadeFrom = P.y + 19 * u, fadeTo = P.y + 28 * u;
      for (const c of chat) {
        const y = slotY(c.k);
        const fade = clamp01((y - fadeFrom) / (fadeTo - fadeFrom));
        bubble(phoneBubbleX(c.m), y, c.m, { alpha: c.a * fade, scale: 0.9 + 0.1 * back(c.a) });
      }
      if (clock < typingUntil) {
        const x = P.x + P.w - 12 * u, y = slotY(0);
        rr(x - 8 * u, y - 4.7 * u, 16 * u, 9.4 * u, 2.8 * u);
        ctx.fillStyle = '#10261a'; ctx.fill(); ctx.strokeStyle = rgba(ACC, 0.3); ctx.stroke();
        for (let i = 0; i < 3; i++) {
          const a = 0.35 + 0.65 * Math.max(0, Math.sin(clock * 0.012 - i * 0.9));
          ctx.fillStyle = `rgba(255,255,255,${a})`;
          ctx.beginPath(); ctx.arc(x - 3.6 * u + i * 3.6 * u, y, 1 * u, 0, 6.283); ctx.fill();
        }
      }
      ctx.restore();
      ctx.restore();
    }

    // ---- moderação
    if (G.a > 0.01) {
      const ga = gateFlash.a, gw = G.a;
      const half = (G.x1 - G.x0) / 2 * gw;
      ctx.save();
      ctx.globalCompositeOperation = 'lighter';
      const col = ga > 0.02 ? gateFlash.c : ACC;
      const grad = ctx.createLinearGradient(cx - half, 0, cx + half, 0);
      grad.addColorStop(0, rgba(col, 0)); grad.addColorStop(0.5, rgba(col, 0.45 + 0.5 * ga)); grad.addColorStop(1, rgba(col, 0));
      ctx.strokeStyle = grad; ctx.lineWidth = 1.4 + 1.6 * ga;
      ctx.beginPath(); ctx.moveTo(cx - half, G.y); ctx.lineTo(cx + half, G.y); ctx.stroke();
      const run = cx - half + ((t * 0.12) % (2 * half || 1));
      glow(run, G.y, 5 * u, col, 0.35 * gw);
      glow(cx, G.y, 9 * u, col, (0.18 + 0.4 * ga) * gw);
      ctx.restore();
      ctx.save(); ctx.globalAlpha = gw;
      ctx.fillStyle = 'rgba(255,255,255,0.5)'; ctx.font = font(600, 3.1);
      ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText('MODERAÇÃO', G.x0 + 1 * u, G.y - 4.5 * u);
      ctx.restore();
    }

    // ---- TV / tela das lâminas
    glass(R.x, R.y, R.w, R.h, lerp(3.5, 4.5, m) * u, Math.max(tvGlow, m * 0.6), T.a);
    const tvA = T.a * (1 - clamp01(m * 3));          // conteúdo da TV some rápido no começo da transformação
    ctx.save(); ctx.globalAlpha = T.a * (1 - m);
    ctx.strokeStyle = 'rgba(255,255,255,0.18)'; ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(R.x + R.w / 2, R.y + R.h); ctx.lineTo(R.x + R.w / 2, R.y + R.h + 5 * u);
    ctx.moveTo(R.x + R.w / 2 - 14 * u, R.y + R.h + 5 * u); ctx.lineTo(R.x + R.w / 2 + 14 * u, R.y + R.h + 5 * u); ctx.stroke();
    ctx.restore();
    if (tvA > 0.01) {
      ctx.save(); ctx.globalAlpha = tvA;
      const live = 0.5 + 0.5 * Math.sin(t * 0.006);
      rr(T.x + 4 * u, T.y + 3.4 * u, 19 * u, 6 * u, 3 * u);
      ctx.fillStyle = rgba(ACC, 0.14 + 0.08 * live); ctx.fill();
      ctx.fillStyle = rgba(ACC, 0.5 + 0.5 * live);
      ctx.beginPath(); ctx.arc(T.x + 8 * u, T.y + 6.4 * u, 1.3 * u, 0, 6.283); ctx.fill();
      ctx.fillStyle = rgba(ACC, 1); ctx.font = font(700, 3.2); ctx.textAlign = 'left'; ctx.textBaseline = 'middle';
      ctx.fillText('AO VIVO', T.x + 10.8 * u, T.y + 6.5 * u);
      ctx.textAlign = 'right';
      ctx.fillStyle = 'rgba(255,255,255,0.92)'; ctx.font = font(700, 3.6);
      ctx.fillText(Math.round(shownTotal).toLocaleString('pt-BR'), T.x + T.w - 4 * u, T.y + 5.4 * u);
      ctx.fillStyle = 'rgba(255,255,255,0.4)'; ctx.font = font(500, 2.4);
      ctx.fillText('mensagens recebidas', T.x + T.w - 4 * u, T.y + 9 * u);
      ctx.save();
      rr(T.x + 1, T.y + 11.5 * u, T.w - 2, T.h - 11.5 * u - 1, 3.5 * u); ctx.clip();
      for (const c of cards) {
        const y = cardY(c.k), x = T.x + 4 * u, w = T.w - 8 * u, h = cardH();
        const alpha = c.a * clamp01(3.0 - c.k);
        ctx.save(); ctx.globalAlpha = alpha * tvA;
        rr(x, y, w, h, 2.4 * u);
        ctx.fillStyle = c.k < 0.5 ? rgba(ACC, 0.08) : 'rgba(255,255,255,0.04)'; ctx.fill();
        ctx.strokeStyle = c.k < 0.5 ? rgba(ACC, 0.55) : 'rgba(255,255,255,0.07)'; ctx.stroke();
        const ag = ctx.createLinearGradient(x + 3 * u, y, x + 11 * u, y + h);
        ag.addColorStop(0, rgba(ACC, 0.9)); ag.addColorStop(1, rgba(TEAL, 0.9));
        ctx.fillStyle = ag;
        ctx.beginPath(); ctx.arc(x + 7 * u, y + h / 2, 3.8 * u, 0, 6.283); ctx.fill();
        ctx.fillStyle = '#0a0a0a'; ctx.font = font(700, 3.6); ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(c.m.n[0], x + 7 * u, y + h / 2 + 0.2 * u);
        ctx.textAlign = 'left';
        ctx.fillStyle = rgba(ACC, 0.95); ctx.font = font(600, 3.4);
        ctx.fillText(c.m.n, x + 13.5 * u, y + 4.2 * u);
        ctx.fillStyle = 'rgba(255,255,255,0.95)'; ctx.font = font(500, 4.4);
        ctx.fillText(fit(c.m.t, w - 17 * u), x + 13.5 * u, y + 8.8 * u);
        ctx.restore();
      }
      ctx.restore();
      ctx.restore();
    }

    // ---- TV no estúdio: lâminas da vitrine + vinheta, reflexo no chão e placa NO AR
    if (m > 0.001) {
      const scr = renderScreen();
      if (scr.has) main.drawImage(off, R.x, R.y, R.w, R.h);
      if (scr.edge !== null) {
        const ex = R.x + scr.edge * (R.w / S0.w);
        if (ex > R.x && ex < R.x + R.w) {
          for (let n = 0; n < 4; n++) parts.push({ x: ex + rnd(-2, 2) * u, y: R.y + rnd(0.05, 0.95) * R.h, vx: rnd(0.02, 0.09) * u, vy: rnd(-0.02, 0.02) * u, t: 0, max: rnd(300, 650), c: Math.random() < 0.5 ? ACC : WHITE, r: rnd(0.3, 0.8) * u });
        }
      }
      // reflexo: a própria tela espelhada, sumindo num degradê
      rctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      rctx.globalCompositeOperation = 'source-over';
      rctx.clearRect(0, 0, S0.w, S0.h);
      rctx.save(); rctx.translate(0, S0.h); rctx.scale(1, -1); rctx.drawImage(off, 0, 0, S0.w, S0.h); rctx.restore();
      rctx.globalCompositeOperation = 'destination-in';
      const rg = rctx.createLinearGradient(0, 0, 0, S0.h * 0.55);
      rg.addColorStop(0, 'rgba(0,0,0,0.5)'); rg.addColorStop(1, 'rgba(0,0,0,0)');
      rctx.fillStyle = rg; rctx.fillRect(0, 0, S0.w, S0.h);
      rctx.globalCompositeOperation = 'source-over';
      main.save(); main.globalAlpha = m * 0.55; main.drawImage(refl, R.x, R.y + R.h + 2.5 * u, R.w, R.h); main.restore();
      // brilho do "chão" do estúdio
      main.save(); main.globalCompositeOperation = 'lighter';
      const fl = main.createLinearGradient(R.x, 0, R.x + R.w, 0);
      fl.addColorStop(0, rgba(ACC, 0)); fl.addColorStop(0.5, rgba(ACC, 0.35 * m)); fl.addColorStop(1, rgba(ACC, 0));
      main.fillStyle = fl; main.fillRect(R.x, R.y + R.h + 1.6 * u, R.w, 1.2);
      main.restore();

      // placa NO AR acima da TV
      const na = clamp01((m - 0.5) * 2);
      if (na > 0) {
        const RED2 = [239, 68, 68];
        const sx = R.x + R.w / 2, sy = R.y - 10 * u;
        const pulse = 0.6 + 0.4 * Math.sin(clock * 0.005);
        main.save(); main.globalAlpha = na;
        main.font = font(800, 4.4);
        const tw = main.measureText('NO AR').width, bw = tw + 13 * u, bh = 9 * u;
        rr(sx - bw / 2, sy - bh / 2, bw, bh, 1.8 * u);
        main.shadowColor = rgba(RED2, 0.9 * pulse); main.shadowBlur = 26 * pulse;
        main.fillStyle = 'rgba(45,6,12,0.95)'; main.fill();
        main.shadowBlur = 0;
        main.strokeStyle = rgba(RED2, 0.95); main.lineWidth = 1.4; main.stroke();
        main.fillStyle = rgba(RED2, 0.55 + 0.45 * pulse);
        main.beginPath(); main.arc(sx - bw / 2 + 4.6 * u, sy, 1.5 * u, 0, 6.283); main.fill();
        main.fillStyle = '#fecaca'; main.textAlign = 'left'; main.textBaseline = 'middle';
        main.fillText('NO AR', sx - bw / 2 + 7.8 * u, sy + 0.3 * u);
        main.restore();
        main.save(); main.globalCompositeOperation = 'lighter';
        glow(sx, sy, 22 * u, RED2, 0.12 * na * pulse);
        main.restore();
      }
    }

    // ---- mensagens em trânsito
    for (const f of flights) {
      const bw = bubbleW(f.m);
      let pos, alpha = 1, scale = 1, rot = 0, stroke, lit = 0.5;
      const after = f.age - TO_GATE - DWELL;
      if (f.age < TO_GATE) {
        pos = qb(from, c1, gatePos, ease(f.age / TO_GATE));
        alpha = clamp01((f.age - 40) / 200);
        scale = 0.85 + 0.15 * clamp01(f.age / 300);
      } else if (after < 0) {
        pos = gatePos;
        const s = clamp01((f.age - TO_GATE) / (DWELL * 0.55));
        if (s < 1) {
          const sx = pos.x - bw / 2 + bw * s;
          ctx.save(); ctx.globalCompositeOperation = 'lighter';
          glow(sx, pos.y, 7 * u, WHITE, 0.35);
          ctx.strokeStyle = 'rgba(255,255,255,0.9)'; ctx.lineWidth = 1.6;
          ctx.beginPath(); ctx.moveTo(sx, pos.y - 7 * u); ctx.lineTo(sx, pos.y + 7 * u); ctx.stroke();
          ctx.restore();
        }
        if (f.decided) { stroke = f.m.spam ? rgba(RED, 0.9) : rgba(ACC, 0.9); lit = 1; }
      } else if (f.m.spam) {
        const d = clamp01(after / DROP), e = ease(d);
        pos = { x: gatePos.x + 20 * u * e, y: gatePos.y + 4 * u * e };
        alpha = 1 - d; scale = 1 - 0.3 * d; rot = 0.25 * e; stroke = rgba(RED, 0.9); lit = 1 - d;
      } else {
        const d = clamp01(after / TO_TV);
        pos = qb(gatePos, c2, tvIn, ease(d));
        scale = lerp(1, 0.92, d); alpha = d > 0.8 ? 1 - (d - 0.8) / 0.2 : 1; stroke = rgba(ACC, 0.9); lit = 1;
      }
      f.trail.push({ x: pos.x, y: pos.y });
      if (f.trail.length > 14) f.trail.shift();
      if (f.trail.length > 2 && !(f.m.spam && after > 0)) {
        ctx.save(); ctx.globalCompositeOperation = 'lighter';
        for (let i = 1; i < f.trail.length; i++) {
          const k = i / f.trail.length, a = f.trail[i - 1], b = f.trail[i];
          ctx.strokeStyle = rgba(f.decided && f.m.spam ? RED : ACC, 0.5 * k * alpha);
          ctx.lineWidth = 6 * u * k; ctx.lineCap = 'round';
          ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();
        }
        ctx.restore();
        if (Math.random() < 0.5) parts.push({ x: pos.x + rnd(-bw / 3, bw / 3), y: pos.y + rnd(-2, 2) * u, vx: rnd(-0.01, 0.01) * u, vy: rnd(-0.01, 0.02) * u, t: 0, max: rnd(300, 600), c: ACC, r: rnd(0.3, 0.6) * u });
      }
      bubble(pos.x, pos.y, f.m, { alpha, scale, rot, stroke, lit });
      if (f.decided) {
        const bs = back(clamp01((f.age - TO_GATE - DWELL * 0.55) / 260));
        badge(pos.x + (bw / 2) * scale, pos.y - 4.6 * u * scale, !f.m.spam, bs, alpha);
      }
    }

    // ---- partículas e ondas de choque
    ctx.save(); ctx.globalCompositeOperation = 'lighter';
    for (const w of waves) {
      const e = easeOut(w.t);
      ctx.strokeStyle = rgba(ACC, 0.6 * (1 - w.t)); ctx.lineWidth = 2 * (1 - w.t) + 0.5;
      ctx.beginPath(); ctx.ellipse(w.x, w.y, 10 * u + 45 * u * e, 4 * u + 16 * u * e, 0, 0, 6.283); ctx.stroke();
    }
    for (const p of parts) {
      const k = 1 - p.t / p.max;
      ctx.fillStyle = rgba(p.c, 0.25 * k); ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 3, 0, 6.283); ctx.fill();
      ctx.fillStyle = rgba(p.c, 0.95 * k); ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 6.283); ctx.fill();
    }
    ctx.restore();

    canvas.style.transform = `rotateY(${mx * 7}deg) rotateX(${-my * 5}deg)`;
  }

  function frame(time) {
    const dt = Math.min(64, time - (last || time));
    last = time;
    update(dt); draw();
    raf = requestAnimationFrame(frame);
  }
  function play() { if (running || reduce) return; running = true; last = 0; raf = requestAnimationFrame(frame); }
  function pause() { running = false; cancelAnimationFrame(raf); }

  const onMove = e => { tmx = (e.clientX / window.innerWidth) * 2 - 1; tmy = (e.clientY / window.innerHeight) * 2 - 1; };
  if (!reduce) window.addEventListener('mousemove', onMove, { passive: true });

  layout();
  if (reduce) {
    // quadro estático: a primeira lâmina já no ar
    clock = 3000; setScene('show'); sceneT = 1500;
    update(0); parts = []; streams = [];
    draw();
  }

  const ro = new ResizeObserver(() => { layout(); if (reduce) { update(0); draw(); } });
  ro.observe(canvas.parentElement);
  const io = new IntersectionObserver(([e]) => (e.isIntersecting && !document.hidden ? play() : pause()));
  io.observe(canvas.parentElement);
  const onVis = () => (document.hidden ? pause() : play());
  document.addEventListener('visibilitychange', onVis);

  return () => {
    pause(); ro.disconnect(); io.disconnect();
    document.removeEventListener('visibilitychange', onVis);
    window.removeEventListener('mousemove', onMove);
  };
}
